// src/components/shared/DonutChart.jsx
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const DonutChart = ({
  data = [], // [{ label, value, color, emoji }]
  size = 200,
  strokeWidth = 30,
  centerContent = null,
  showLegend = true,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  // Calculate total and percentages
  const total = useMemo(() =>
    data.reduce((sum, item) => sum + item.value, 0),
    [data]
  );

  // Create segments
  const segments = useMemo(() => {
    let currentOffset = 0;

    return data.map((item, index) => {
      const percentage = (item.value / total) * 100;
      const segmentLength = (percentage / 100) * circumference;
      const offset = currentOffset;
      currentOffset += segmentLength;

      return {
        ...item,
        percentage,
        segmentLength,
        offset,
        dashArray: `${segmentLength} ${circumference - segmentLength}`,
        dashOffset: -offset,
      };
    });
  }, [data, total, circumference]);

  const colorMap = {
    teal: '#14b8a6',
    purple: '#a855f7',
    green: '#22c55e',
    orange: '#f97316',
    blue: '#3b82f6',
    yellow: '#eab308',
    pink: '#ec4899',
    red: '#ef4444',
  };

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Chart */}
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            className="text-slate-800/30"
          />

          {/* Segments */}
          {segments.map((segment, index) => (
            <motion.circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colorMap[segment.color] || segment.color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={segment.dashArray}
              strokeDashoffset={0}
              strokeLinecap="round"
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: segment.dashOffset }}
              transition={{ duration: 1.5, delay: index * 0.1, ease: 'easeOut' }}
              style={{
                filter: `drop-shadow(0 0 6px ${colorMap[segment.color] || segment.color}40)`,
              }}
            />
          ))}
        </svg>

        {/* Center content */}
        {centerContent && (
          <div className="absolute inset-0 flex items-center justify-center">
            {centerContent}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="grid grid-cols-2 gap-3 w-full">
          {segments.map((segment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 + 0.5 }}
              className="flex items-center gap-2"
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: colorMap[segment.color] || segment.color,
                  boxShadow: `0 0 8px ${colorMap[segment.color] || segment.color}40`,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white flex items-center gap-1">
                  {segment.emoji && <span>{segment.emoji}</span>}
                  <span className="truncate">{segment.label}</span>
                </div>
                <div className="text-xs text-slate-400">
                  {Math.round(segment.percentage)}%
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
